import { RestClientService } from '../rest-client.service';
import { ActionDataService } from './data.service';
import { ObservableResult } from '../../models/result.model';
import {
  ComposerOfferOrOrder, CustomAddress,
  forwardRequest,
  IBrokeringTargetsResponse,
  IActionResponse, IActionStudioSearchResult,
  IActionTypes,
  IFrowardUsers,
  IGetAppointments, LeadAction, NotInterestedDetail, NotInterestedTypes, PlaceInOrder
} from '../../models/action.model';
import { IFileDocument } from '../../models/shared.model';
import { AppointmentExtProp, OfferExtProp, SelectedComposerOffers } from "../../models/leads/leads.model";
import { TextHelper } from '../../helper/text.helper';

export class ApiActionDataService implements ActionDataService {

  constructor(
    private client: RestClientService
  ) {
  }

  getSnippets(): ObservableResult<LeadAction[]> {
    const url = '/api/Action';
    const method = 'GET';

    return this.client.requestWithConstUrl<LeadAction[]>(method, url);
  }

  sendKkz( leadId: number, comment: string, kkz: string, externalEmail: string ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'LEAD_FORWARD_EXT',
      ExternalKKZ: kkz,
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined,
      ExternalEmail: externalEmail,
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  updateIsSharedStatus( leadId: number, isShared: boolean ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: isShared ? 'LEAD_SHARE' : 'LEAD_SHARE_STOP',
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  sendEmail( leadId: number, comment: string, externalEmail: string ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'LEAD_FORWARD_EXT',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined,
      ExternalEmail: externalEmail,
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  getAppointments( userId: number ): ObservableResult<IGetAppointments> {
    const url = '/api/UserProfile/Appointments/';
    const method = 'GET';
    return this.client.requestWithConstUrl<IGetAppointments>(method, url, userId);
  }

  getRejectTypes(): ObservableResult<IActionTypes[]> {
    const url = '/api/Action/RejectTypes';
    const method = 'GET';

    return this.client.requestWithConstUrl<IActionTypes[]>(method, url);
  }

  getNotInterestedTypes(leadTypeId?: number): ObservableResult<NotInterestedTypes[] | IActionTypes[]> {
    const url = '/api/Action/NotInterestedTypes';
    const method = 'GET';
    const body = leadTypeId ? { leadTypeId } : null;

    return this.client.requestWithConstUrl<NotInterestedTypes[] | IActionTypes[]>(method, url, body);
  }

  getNotInterestedDetails(): ObservableResult<NotInterestedDetail[]> {
    const url = '/api/Action/NotInterestedDetails';
    const method = 'GET';

    return this.client.requestWithConstUrl<NotInterestedDetail[]>(method, url);
  }

  getAppointmentTypes(): ObservableResult<IActionTypes[]> {
    const url = '/api/Action/AppointementTypes';
    const method = 'GET';

    return this.client.requestWithConstUrl<IActionTypes[]>(method, url);
  }

  getForwardUsers( leadId: number, leadTypeId: number ): ObservableResult<IFrowardUsers> {
    const url = '/api/Action/ForwardUsers';
    const method = 'GET';
    const body = { leadId, leadTypeId };

    return this.client.requestWithConstUrl<IFrowardUsers>(method, url, body);
  }

  forwardRequest( leadId: number, comment: string, forwardUserId: number, forwardLeadTypeId: number, externalEmail: string ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const actionName = !!forwardUserId ? 'LEAD_FORWARD' : 'LEAD_FORWARD_EXT';

    const body: forwardRequest = {
      LeadId: leadId,
      ActionName: actionName,
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined,
      ForwardLeadTypeId: forwardLeadTypeId,
    };

    !!forwardUserId ? body.ForwardUserId = forwardUserId : body.ExternalEmail = externalEmail;

    return this.client.requestWithConstUrl<any>(method, url, body);
  }

  rejectRequest( leadId: number, comment: string, rejectReasonId: number ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'LEAD_REJECT',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined,
      RejectReasonId: rejectReasonId
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  confirmRequest( leadId: number ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'LEAD_FORWARD_CONFIRM',
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  completeRequest( leadId: number, comment: string, isExternal?: boolean ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: isExternal ? 'LEAD_PROCESS_EXT' : 'LEAD_PROCESS',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  closeRequest( leadId: number, comment: string, notInterestedTypeId: number, notInterestedDetailId?: number ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'LEAD_NOT_INTERESTED',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined,
      NotInterestedTypeId: notInterestedTypeId,
      NotInterestedDetailId: notInterestedDetailId || undefined
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  submitOffer(
    leadId: number,
    comment: string,
    appointmentTypeId: number,
    appointmentDate: Date,
    extendedProperties: OfferExtProp[] | OfferExtProp | SelectedComposerOffers,
    isComposerOffer: boolean,
    showroomTypeId?: string,
    isExternal: boolean = false
  ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: isExternal ? 'OFFER_SUBMIT_EXTERNAL' : 'OFFER_SUBMIT',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined,
      AppointmentTypeId: appointmentTypeId,
      AppointmentDate: appointmentDate,
    };
    if ( showroomTypeId ) {
      body['StudioKKZ'] = showroomTypeId;
    }

    if ( isComposerOffer ) {
      body['SelectedComposerOffers'] = extendedProperties;
    } else {
      body['ExtendedProperties'] = extendedProperties;
    }

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  submitComposerOfferOrder( leadId: number, data: ComposerOfferOrOrder ): ObservableResult<IActionResponse> {
    const url = '/api/Leads/ComposerOffers/' + leadId;
    const method = 'PUT';

    return this.client.requestWithConstUrl<IActionResponse>(method, url, data);
  }

  deleteComposerOfferOrder( leadId: number, offerId: number ): ObservableResult<IActionResponse> {
    const url = `/api/Leads/ComposerOffers/${leadId}/${offerId}`;
    const method = 'DELETE';

    return this.client.requestWithConstUrl<IActionResponse>(method, url);
  }

  composerOfferSetToMain( leadId: number, offerId: number ): ObservableResult<IActionResponse> {
    const url = `/api/Leads/SetMainComposerOffer/${leadId}/${offerId}`;
    const method = 'POST';

    return this.client.requestWithConstUrl<IActionResponse>(method, url);
  }

  composerMultiOffersSetToMain( leadId: number, offerIds: number[] ): ObservableResult<IActionResponse> {
    const url = `/api/Leads/SetMainComposerOffers/${leadId}`;
    const method = 'POST';

    return this.client.requestWithConstUrl<IActionResponse>(method, url, offerIds);
  }

  undoAssignment( leadId: number, comment: string ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'LEAD_FORWARD_UNDO',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  reopenLead( leadId: number, comment: string ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'LEAD_REOPEN',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  placeOrder( leadId: number, placeInOrder: PlaceInOrder, isExternal?: boolean ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: isExternal ? 'ORDER_PLACED_EXTERNAL' : 'ORDER_PLACED',
      Comment: placeInOrder.Comment.trim() ? TextHelper.sanitizeHtml(placeInOrder.Comment) : undefined,
      CustomerCode: placeInOrder.CustomerCode,
      CustomerCodeComposer: placeInOrder.CustomerCodeComposer
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  changeType( leadId: number, comment: string, leadTypeId: number ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'LEAD_TYPE',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined,
      ForwardLeadTypeId: leadTypeId
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  changeSource( leadId: number, leadSourceId: number ): ObservableResult<IActionResponse> {
    const url = `/api/Leads/${leadId}`;
    const method = 'PUT';
    const body = {
      LeadSourceId: leadSourceId
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  createAppointment(
    leadId: number,
    comment: string,
    appointmentTypeId: number,
    appointmentDate: Date,
    extendedProperties: AppointmentExtProp,
    showroomTypeId?: string,
    isExternal: boolean = false
  ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: isExternal ? 'APPOINTMENT_CREATE_EXTERNAL' : 'APPOINTMENT_CREATE',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined,
      AppointmentDate: appointmentDate,
      AppointmentTypeId: appointmentTypeId,
      ExtendedProperties: extendedProperties?.OFFER_AMOUNT ? extendedProperties : undefined
    };
    if ( showroomTypeId ) {
      body['StudioKKZ'] = showroomTypeId;
    }

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  createConsultation(
    leadId: number,
    comment: string,
    appointmentTypeId: number,
    appointmentDate: Date,
    extendedProperties: AppointmentExtProp,
    showroomTypeId?: string,
    isExternal: boolean = false
  ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: isExternal ? 'CONSULTATION_CREATE_EXTERNAL' : 'CONSULTATION_CREATE',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined,
      AppointmentDate: appointmentDate,
      AppointmentTypeId: appointmentTypeId,
      ExtendedProperties: extendedProperties?.OFFER_AMOUNT ? extendedProperties : undefined
    };
    if ( showroomTypeId ) {
      body['StudioKKZ'] = showroomTypeId;
    }

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  moveAppointment( leadId: number, comment: string, appointmentDate: Date ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'APPOINTMENT_MOVE',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined,
      AppointmentDate: appointmentDate,
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  moveOffer( leadId: number, comment: string, appointmentDate: Date ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'OFFER_MOVE',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined,
      AppointmentDate: appointmentDate,
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  visitPlanner( leadId: number, comment: string, appointmentDate: Date, appointmentTypeId: number, isPlanerVisitMove: boolean, isExternal: boolean, address?: CustomAddress ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: isExternal ? 'PLANNER_VISIT_EXT' : isPlanerVisitMove ? 'PLANNER_VISIT_MOVE' : 'PLANNER_VISIT',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined,
      AppointmentDate: appointmentDate,
      CustomAddress: address,
      AppointmentTypeId: appointmentTypeId,
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  invitePlanner( leadId: number, comment: string, appointmentDate: Date, kkz: string, appointmentTypeId: number, isPlanerInviteMove: boolean, isExternal: boolean ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: isExternal ? 'PLANNER_INVITE_EXT' : isPlanerInviteMove ? 'PLANNER_INVITE_MOVE' : 'PLANNER_INVITE',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined,
      AppointmentDate: appointmentDate,
      StudioKKZ: kkz,
      AppointmentTypeId: appointmentTypeId,
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  compendiumAllow( leadId: number, comment: string ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'COMPENDIUM_ALLOW',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }


  compendiumDeny( leadId: number, comment: string ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'COMPENDIUM_DENY',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  leadBrokerAbort( leadId: number, comment: string ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'LEAD_BROKER_ABORT',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  sendReminder( leadId: number, comment: string, appointmentDate: Date ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'LEAD_REMINDER',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined,
      AppointmentDate: appointmentDate,
    };
    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  sendExternalEmail( leadId: number, externalEmail: string ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'LEAD_REMINDER_EXT',
      ExternalEmail: externalEmail,
    };
    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  createComment( leadId: number, comment: string, attachments?: IFileDocument[] ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'COMMENT_CREATE',
      Comment: TextHelper.sanitizeHtml(comment),
      Attachments: attachments,
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  delayOffer( leadId: number, comment: string, appointmentDate: Date ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'OFFER_DELAY',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : '',
      AppointmentDate: appointmentDate,
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  createNegotiation(
    leadId: number,
    comment: string,
    appointmentTypeId: number,
    appointmentDate: Date,
    showroomTypeId?: string,
  ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'NEGOTIATION_CREATE',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined,
      AppointmentDate: appointmentDate,
      AppointmentTypeId: appointmentTypeId
    };

    if ( showroomTypeId ) {
      body['StudioKKZ'] = showroomTypeId;
    }


    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  moveNegotiation( leadId: number, comment: string, appointmentDate: Date ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'NEGOTIATION_MOVE',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined,
      AppointmentDate: appointmentDate,
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  leadTakeover( leadId: number, comment: string ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'LEAD_TAKEOVER',
      Comment: comment?.trim() ? TextHelper.sanitizeHtml(comment) : undefined
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }

  studioSearch( searchText ): ObservableResult<IActionStudioSearchResult[]> {
    const url = `/api/Action/StudioSearch/${encodeURIComponent(searchText)}`;
    const method = 'GET';

    return this.client.requestWithConstUrl<IActionStudioSearchResult[]>(method, url);
  }

  getBrokeringTargets( leadId: number ): ObservableResult<IBrokeringTargetsResponse> {
    const url = '/api/Action/BrokeringTargets';
    const method = 'GET';
    const body = { leadId };

    return this.client.requestWithConstUrl<IBrokeringTargetsResponse>(method, url, body);
  }

  startBrokering( leadId: number, maxAcceptedPartners: number, secondWaveDetailMinutes: number ): ObservableResult<IActionResponse> {
    const url = '/api/Action';
    const method = 'POST';
    const body = {
      LeadId: leadId,
      ActionName: 'LEAD_BROKER_START',
      BrokeringRequest: {
        AcceptCount: maxAcceptedPartners,
        SecondWaveDelayMinutes: secondWaveDetailMinutes
      },
    };

    return this.client.requestWithConstUrl<IActionResponse>(method, url, body);
  }
}
